import React, { useEffect, useState } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { api, formatMoney, formatApiError } from "../../lib/api";
import PriceDisplay from "../../components/PriceDisplay";
import { useAuth } from "../../context/AuthContext";
import { useCurrency } from "../../context/CurrencyContext";
import { Card } from "../../components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Routes, Route, useNavigate } from "react-router-dom";
import { CalendarDays, UserRound, Receipt, MapPin, ShieldCheck, Star, Navigation, Loader2, HandCoins, MessagesSquare } from "lucide-react";
import { carImageUrl } from "../../components/cars/CarCard";
import { toast } from "sonner";
import KycPage from "./KycPage";
import NegotiationsPage from "./NegotiationsPage";
import ReviewDialog from "../../components/ReviewDialog";
import useLiveLocationSharing from "../../lib/useLiveLocationSharing";
import TripScoreDialog, { TripScoreBadge } from "../../components/TripScoreCard";
import { ContactIcons, MessageComposeDialog } from "../../components/admin/ContactPanel";

const nav = [
  { to: "/account", label: "My bookings", icon: Receipt, testId: "nav-customer-bookings", end: true },
  { to: "/account/negotiations", label: "Negotiations", icon: MessagesSquare, testId: "nav-customer-negotiations" },
  { to: "/account/kyc", label: "Identity verification", icon: ShieldCheck, testId: "nav-customer-kyc" },
  { to: "/account/profile", label: "Profile", icon: UserRound, testId: "nav-customer-profile" },
  { to: "/contact", label: "Support", icon: MessagesSquare, testId: "nav-customer-support" },
];

function classifyBookings(list) {
  const now = Date.now();
  const upcoming = [];
  const active = [];
  const past = [];
  for (const b of list) {
    const s = new Date(b.start_date).getTime();
    const e = new Date(b.end_date).getTime();
    if (b.status === "cancelled" || b.status === "declined") past.push(b);
    else if (now < s) upcoming.push(b);
    else if (now >= s && now <= e) active.push(b);
    else past.push(b);
  }
  return { upcoming, active, past };
}

function ShareLocationToggle({ booking }) {
  const { sharing, start, stop, lastPing, error } = useLiveLocationSharing(booking.id);
  return (
    <div className="mt-3 p-3 rounded-md border border-border bg-card" data-testid={`share-location-${booking.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm flex items-center gap-1.5">
            <Navigation size={13} className={sharing ? "text-accent" : "text-muted-foreground"} />
            {sharing ? "Sharing live location" : "Share live location"}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {sharing
              ? `The owner can see your car's location while your trip is active. Last ping ${lastPing ? new Date(lastPing).toLocaleTimeString() : "—"}.`
              : "Let the owner know where the car is. Helps with handover and trust. You can turn it off anytime."}
          </div>
          {error && <div className="text-xs text-destructive mt-1">{error}</div>}
        </div>
        <Button
          size="sm"
          variant={sharing ? "destructive" : "default"}
          onClick={sharing ? stop : start}
          className={sharing ? "" : "bg-accent text-accent-foreground hover:bg-accent/90"}
          data-testid={`share-location-toggle-${booking.id}`}
        >
          {sharing ? "Stop" : "Start"}
        </Button>
      </div>
    </div>
  );
}

function BookingRow({ booking, onReview, onOpenScore, onConfirmHandover, onCancel, isActive, setMessageTarget }) {
  const { currencies } = useCurrency();
  const now = Date.now();
  const ended = new Date(booking.end_date).getTime() < now;
  const canReview = ended && booking.payment_status === "paid" && !booking.reviewed && booking.status !== "cancelled" && booking.status !== "declined" && booking.status !== "refunded";
  const canCancel = !ended && !["cancelled", "declined", "completed"].includes(booking.status);
  const canShare = booking.payment_status === "paid" && (booking.status === "confirmed" || booking.status === "active");
  const needsHandoverConfirm = booking.payment_status === "paid" && booking.escrow_status === "held" && booking.status === "confirmed";
  return (
    <Card className="border-border p-4 flex flex-col md:flex-row gap-4 hover:shadow-organic transition-shadow" data-testid={`booking-row-${booking.id}`}>
      <div className="w-full md:w-32 h-32 md:h-24 rounded-md overflow-hidden bg-muted shrink-0">
        <img src={carImageUrl(booking.car_image)} alt={booking.car_title} className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-display font-semibold">{booking.car_title}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <CalendarDays size={12} /> {booking.start_date} → {booking.end_date} · {booking.days} day{booking.days > 1 ? "s" : ""}
            </div>
            {booking.payment_status === "paid" ? (
              <ContactIcons email={booking.owner_email} phone={booking.owner_phone} userId={booking.owner_id} name="the owner" isGuest={booking.owner_is_guest} setMessageTarget={setMessageTarget} />
            ) : (
              <div className="text-[10px] text-muted-foreground mt-0.5">Owner contact unlocks once the booking fee is paid</div>
            )}
          </div>
          <Badge variant={booking.status === "confirmed" || booking.status === "active" ? "default" : "outline"} className="capitalize">
            {booking.status.replace("_", " ")}
          </Badge>
        </div>
        <div className="mt-2 text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
          <span>Payment: <span className="capitalize font-medium text-foreground">{booking.payment_status}</span></span>
          {booking.reviewed && <Badge variant="outline" className="text-[10px]"><Star size={10} className="mr-1 fill-accent text-accent" /> Reviewed</Badge>}
          {ended && booking.payment_status === "paid" && <TripScoreBadge bookingId={booking.id} onOpen={onOpenScore} />}
        </div>

        {isActive && canShare && <ShareLocationToggle booking={booking} />}
      </div>
      <div className="text-right md:text-right shrink-0 flex flex-col items-end gap-2">
        <div className="font-display text-lg font-bold"><PriceDisplay amount={booking.total} listingCurrency={booking.currency} /></div>
        {booking.display_currency !== booking.currency && (
          <div className="text-xs text-muted-foreground">≈ {formatMoney(booking.display_total, booking.display_currency, currencies)}</div>
        )}
        {canReview && (
          <Button size="sm" variant="outline" onClick={() => onReview(booking)} data-testid={`review-btn-${booking.id}`}>
            <Star size={12} className="mr-1.5" /> Leave a review
          </Button>
        )}
        {canCancel && (
          <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => onCancel(booking)} data-testid={`cancel-btn-${booking.id}`}>
            Cancel booking
          </Button>
        )}
      </div>
    </Card>
  );
}

function BookingsList() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [scoreData, setScoreData] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [messageTarget, setMessageTarget] = useState(null);
  const load = () => {
    setLoading(true);
    api.get("/bookings/mine", { params: { as_role: "customer" } }).then(({ data }) => setBookings(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  // TODO: handover confirmation is not yet implemented (no backend endpoint
  // exists). This stub prevents the "confirmHandover is not defined" crash
  // that was breaking the dashboard for accounts with a paid, escrow-held,
  // confirmed booking.
  const confirmHandover = () => {
    toast.info("Handover confirmation is coming soon.");
  };
  const submitCancel = async () => {
    if (cancelReason.trim().length < 3) {
      toast.error("Please tell us the reason for cancelling (3+ characters)");
      return;
    }
    setCancelling(true);
    try {
      await api.post(`/bookings/${cancelTarget.id}/cancel`, { reason: cancelReason.trim() });
      toast.success("Booking cancelled. Note: the booking fee is non-refundable by default — our team will review your reason and may issue a refund.");
      setCancelTarget(null);
      setCancelReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCancelling(false);
    }
  };

  const { upcoming, active, past } = classifyBookings(bookings);
  if (loading) return <div className="text-muted-foreground">Loading bookings…</div>;

  return (
    <>
      <Tabs defaultValue="upcoming" data-testid="customer-bookings-tabs">
        <TabsList>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming ({upcoming.length})</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">Active ({active.length})</TabsTrigger>
          <TabsTrigger value="past" data-testid="tab-past">Past ({past.length})</TabsTrigger>
        </TabsList>
        {[["upcoming", upcoming, false], ["active", active, true], ["past", past, false]].map(([k, l, isActive]) => (
          <TabsContent key={k} value={k} className="space-y-3 mt-4">
            {l.length === 0 ? (
              <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-8 text-center">
                No bookings here yet.
              </div>
            ) : l.map((b) => (
              <BookingRow
                key={b.id}
                booking={b}
                onReview={setReviewBooking}
                onOpenScore={setScoreData}
                onConfirmHandover={confirmHandover}
                onCancel={setCancelTarget}
                isActive={isActive}
                setMessageTarget={setMessageTarget}
              />
            ))}
          </TabsContent>
        ))}
      </Tabs>
      <ReviewDialog
        open={Boolean(reviewBooking)}
        onOpenChange={(o) => !o && setReviewBooking(null)}
        booking={reviewBooking}
        onSubmitted={load}
      />
      <TripScoreDialog
        open={Boolean(scoreData)}
        onOpenChange={(o) => !o && setScoreData(null)}
        score={scoreData}
      />
      <Dialog open={Boolean(cancelTarget)} onOpenChange={(o) => { if (!o) { setCancelTarget(null); setCancelReason(""); } }}>
        <DialogContent data-testid="cancel-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Cancel &ldquo;{cancelTarget?.car_title}&rdquo;?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">The booking fee is non-refundable by default. Tell us why you need to cancel — our team reviews every request and may issue a refund.</p>
            <Textarea
              rows={4}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. My travel plans changed and I no longer need the car."
              data-testid="cancel-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelTarget(null); setCancelReason(""); }}>Keep booking</Button>
            <Button variant="destructive" onClick={submitCancel} disabled={cancelling} data-testid="cancel-confirm-btn">
              {cancelling ? "Cancelling…" : "Cancel booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MessageComposeDialog messageTarget={messageTarget} setMessageTarget={setMessageTarget} />
      <BecomeHostCallout />
    </>
  );
}

function BecomeHostCallout() {
  const { user, refresh } = useAuth();
  const nav2 = useNavigate();
  const [loading, setLoading] = useState(false);
  if (!user || user.role !== "customer") return null;
  const upgrade = async () => {
    setLoading(true);
    try {
      await api.post("/users/me/become-host");
      await refresh();
      toast.success("You're now a host! Let's list your first car.");
      nav2("/owner/cars/new");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  return (
    <Card className="border-border p-6 mt-8 bg-gradient-to-br from-[#2A9D8F] to-[#23887A] text-white rounded-2xl" data-testid="become-host-callout">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/70 mb-1">For owners</div>
          <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight">Have a car? Earn on RydeXpress.</h3>
          <p className="text-sm text-white/80 mt-1.5">Upgrade your account to host. You&rsquo;ll need to upload your Carte Jaune, Assurance, and Contrôle Technique, then your car goes live.</p>
        </div>
        <Button
          onClick={upgrade}
          disabled={loading}
          className="bg-[#E97A48] hover:bg-[#D86A38] text-white rounded-full px-6 h-11 btn-lift shrink-0"
          data-testid="become-host-btn"
        >
          {loading ? "Upgrading…" : "Become a host →"}
        </Button>
      </div>
    </Card>
  );
}

function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    country: user?.country || "",
    currency_pref: user?.currency_pref || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: uploaded } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch("/users/me", { profile_image_url: uploaded.url });
      await refresh();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingPhoto(false);
      e.target.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const nameParts = form.name.trim().split(/\s+/).filter(Boolean);
    if (nameParts.length < 2) {
      toast.error("Please enter both your first and last name.");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/users/me", form);
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-border p-6 max-w-2xl">
      <h3 className="font-display text-xl font-semibold tracking-tight">Personal info</h3>
      <div className="mt-4 flex items-center gap-4">
        <Avatar className="h-16 w-16">
          {user?.profile_image_url && <AvatarImage src={user.profile_image_url} alt={user?.name} />}
          <AvatarFallback className="bg-primary text-primary-foreground text-lg">
            {user?.name?.charAt(0)?.toUpperCase() || "U"}
          </AvatarFallback>
        </Avatar>
        <div>
          <Label htmlFor="profile-photo-input" className="inline-block cursor-pointer text-sm font-medium text-primary hover:underline">
            {uploadingPhoto ? "Uploading…" : "Change photo"}
          </Label>
          <input
            id="profile-photo-input"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={uploadPhoto}
            disabled={uploadingPhoto}
            data-testid="profile-photo-input"
          />
        </div>
      </div>
      <form onSubmit={save} className="mt-6 space-y-4" data-testid="profile-form">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" /></div>
          <div><Label>Phone</Label><Input className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^0-9+\s]/g, "") })} inputMode="tel" data-testid="profile-phone-input" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Country</Label><Input className="mt-1.5" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="profile-country-input" /></div>
          <div><Label>Preferred currency</Label><Input className="mt-1.5" value={form.currency_pref} onChange={(e) => setForm({ ...form, currency_pref: e.target.value })} data-testid="profile-currency-input" /></div>
        </div>
        <div className="text-xs text-muted-foreground">Email: {user?.email}</div>
        <Button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="profile-save-btn">{saving ? "Saving…" : "Save changes"}</Button>
      </form>
    </Card>
  );
}

export default function CustomerDashboard() {
  return (
    <DashboardLayout nav={nav} title="My dashboard" subtitle="Your trips and account">
      <Routes>
        <Route index element={<BookingsList />} />
        <Route path="negotiations" element={<NegotiationsPage />} />
        <Route path="kyc" element={<KycPage />} />
        <Route path="profile" element={<Profile />} />
      </Routes>
    </DashboardLayout>
  );
}
